import { useEffect, useMemo, useState } from 'react';
import { FiTrash2 } from 'react-icons/fi';
import {
  deleteNewsletterSubscription,
  getNewsletterSubscriptions,
} from '../api/newsletterSubscriptionsApi';
import ConfirmDialog from '../components/dashboard/ConfirmDialog';
import {
  NEWSLETTER_SOURCE_LABELS,
  getNewsletterSourceLabel,
} from '../utils/newsletterSources';

const formatDate = (value) => {
  if (!value) {
    return '—';
  }

  return new Date(value).toLocaleString();
};

const NewsletterSubscriptionsPage = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [subscriptionToDelete, setSubscriptionToDelete] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const loadSubscriptions = async () => {
    setIsLoading(true);
    setError('');

    try {
      const data = await getNewsletterSubscriptions();
      setSubscriptions(data);
    } catch (err) {
      setSubscriptions([]);
      setError(err.message || 'Failed to load subscriptions.');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadSubscriptions();
  }, []);

  const sourceOptions = useMemo(
    () => [
      { value: '', label: 'All sources' },
      ...Object.entries(NEWSLETTER_SOURCE_LABELS).map(([value, label]) => ({
        value,
        label,
      })),
    ],
    []
  );

  const filteredSubscriptions = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();

    return subscriptions.filter((subscription) => {
      if (sourceFilter && subscription.source !== sourceFilter) {
        return false;
      }

      if (query && !subscription.email.toLowerCase().includes(query)) {
        return false;
      }

      if (dateFrom) {
        const from = new Date(`${dateFrom}T00:00:00`);
        if (new Date(subscription.createdAt) < from) {
          return false;
        }
      }

      if (dateTo) {
        const to = new Date(`${dateTo}T23:59:59.999`);
        if (new Date(subscription.createdAt) > to) {
          return false;
        }
      }

      return true;
    });
  }, [subscriptions, searchQuery, sourceFilter, dateFrom, dateTo]);

  const hasActiveFilters = searchQuery || sourceFilter || dateFrom || dateTo;

  const clearFilters = () => {
    setSearchQuery('');
    setSourceFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const handleDeleteClick = (subscription) => {
    setSubscriptionToDelete(subscription);
  };

  const handleDeleteCancel = () => {
    if (deletingId) {
      return;
    }

    setSubscriptionToDelete(null);
  };

  const handleDeleteConfirm = async () => {
    if (!subscriptionToDelete) {
      return;
    }

    setDeletingId(subscriptionToDelete.id);
    setError('');

    try {
      await deleteNewsletterSubscription(subscriptionToDelete.id);
      setSubscriptions((current) =>
        current.filter((item) => item.id !== subscriptionToDelete.id)
      );
      setSubscriptionToDelete(null);
    } catch (err) {
      setError(err.message || 'Failed to delete subscription.');
    } finally {
      setDeletingId(null);
    }
  };

  if (isLoading) {
    return <p className="text-sm text-[#6b635c]">Loading subscriptions…</p>;
  }

  return (
    <div className="mx-auto max-w-6xl">
      <header className="mb-6 border-b border-[#e4d9c8] pb-5">
        <p className="text-[12px] font-medium uppercase tracking-[0.16em] text-[#6b635c]">
          Submissions
        </p>
        <h2 className="mt-2 font-[Merriweather] text-[1.5rem] text-[#130705]">
          Newsletter subscriptions
        </h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-[#6b635c]">
          Review and manage email sign-ups from insights, market analysis, economic calendar, and
          article pages.
        </p>
      </header>

      {error ? (
        <p className="mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          {error}
        </p>
      ) : null}

      <section className="mb-5 rounded-md border border-[#e4d9c8] bg-white p-4 sm:p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm font-medium text-[#130705]">Filters</p>
          {hasActiveFilters ? (
            <button
              type="button"
              onClick={clearFilters}
              className="text-[13px] font-medium text-[#785E32] hover:underline"
            >
              Clear filters
            </button>
          ) : null}
        </div>

        <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
          <label className="block md:col-span-2">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Search</span>
            <input
              type="search"
              value={searchQuery}
              onChange={(event) => setSearchQuery(event.target.value)}
              placeholder="Email address"
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">Source</span>
            <select
              value={sourceFilter}
              onChange={(event) => setSourceFilter(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            >
              {sourceOptions.map((option) => (
                <option key={option.value || 'all'} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">From date</span>
            <input
              type="date"
              value={dateFrom}
              onChange={(event) => setDateFrom(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>

          <label className="block">
            <span className="mb-1.5 block text-[12px] font-medium text-[#6b635c]">To date</span>
            <input
              type="date"
              value={dateTo}
              onChange={(event) => setDateTo(event.target.value)}
              className="w-full rounded-md border border-[#e4d9c8] bg-white px-3 py-2 text-sm text-[#130705] outline-none focus:border-[#c9b48a]"
            />
          </label>
        </div>

        <p className="mt-4 text-[12px] text-[#9a9288]">
          Showing {filteredSubscriptions.length} of {subscriptions.length} subscriptions
        </p>
      </section>

      {subscriptions.length === 0 ? (
        <p className="text-sm text-[#6b635c]">No subscriptions yet.</p>
      ) : filteredSubscriptions.length === 0 ? (
        <p className="text-sm text-[#6b635c]">No subscriptions match the current filters.</p>
      ) : (
        <div className="overflow-x-auto rounded-md border border-[#e4d9c8] bg-white">
          <table className="min-w-full text-left text-sm">
            <thead className="border-b border-[#e4d9c8] bg-[#faf8f4] text-[12px] uppercase tracking-[0.12em] text-[#6b635c]">
              <tr>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Source</th>
                <th className="px-4 py-3 font-medium">Subscribed</th>
                <th className="px-4 py-3 font-medium text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredSubscriptions.map((subscription) => (
                <tr key={subscription.id} className="border-b border-[#f0e8dc] last:border-b-0">
                  <td className="px-4 py-3 font-medium text-[#130705]">{subscription.email}</td>
                  <td className="px-4 py-3 text-[#4a4340]">
                    {getNewsletterSourceLabel(subscription.source)}
                  </td>
                  <td className="px-4 py-3 text-[#6b635c]">
                    {formatDate(subscription.createdAt)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end">
                      <button
                        type="button"
                        onClick={() => handleDeleteClick(subscription)}
                        disabled={deletingId === subscription.id}
                        className="inline-flex items-center gap-1.5 rounded-md !border !border-[#e4d9c8] !bg-white px-3 py-1.5 text-[12px] font-medium !text-[#6b635c] !shadow-none transition hover:!border-[#fecdca] hover:!bg-[#fef3f2] hover:!text-[#b42318] hover:!translate-y-0 hover:!shadow-none disabled:opacity-60"
                      >
                        <FiTrash2 className="h-3.5 w-3.5" aria-hidden="true" />
                        {deletingId === subscription.id ? 'Deleting…' : 'Delete'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <ConfirmDialog
        isOpen={Boolean(subscriptionToDelete)}
        title="Delete subscription?"
        message={
          subscriptionToDelete
            ? `Delete ${subscriptionToDelete.email}? This cannot be undone.`
            : ''
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        isLoading={Boolean(deletingId)}
        onClose={handleDeleteCancel}
        onConfirm={handleDeleteConfirm}
      />
    </div>
  );
};

export default NewsletterSubscriptionsPage;
